import React, { useState, useEffect } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { useAuth } from "../hooks/useAuth";
import { Link, useNavigate } from "react-router-dom";
import { 
  Card, 
  Table, 
  Row, 
  Col, 
  Form, 
  InputGroup, 
  Button, 
  Pagination, 
  Badge, 
  Modal, 
  Toast,
  ToastContainer,
  Spinner,
  FloatingLabel,
  ListGroup
} from "react-bootstrap";
import axios from "axios";
import {
  Search,
  PlusCircle,
  Eye,
  Pencil,
  Trash,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  ExclamationCircle,
  Layers,
  Map,
  Globe,
  Building,
  House,
  Folder,
  FolderPlus,
  InfoCircle,
  ArrowLeft,
  GeoAlt,
  PinMap,
  PinMapFill,
  PlusLg,
  ChevronRight,
  Diagram3,
  Search as SearchIcon,
  Cloud,
  BarChartFill
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../constants/api_constants";


// Configuration de l'API

const TYPES_ENDPOINT = `${API_BASE_URL}/localites/types`;
const LOCALITES_ENDPOINT = `${API_BASE_URL}/localites`;

// Données statiques des localités
const LOCALITES_STATIQUES = [];

export default function Localites() {
  const navigate = useNavigate();
  const country_code = sessionStorage.getItem("country_code") || 'GA';

  // Rôle de l'utilisateur
  const { isAuthenticated, user, loading: authLoading, logout } = useAuth();
  const userRole = user?.role?.name || 'agent';
  
  // États principaux
  const [localites, setLocalites] = useState([]);
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [sortField, setSortField] = useState('nom');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedLocalites, setSelectedLocalites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // États pour les modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // États pour les données des modals
  const [selectedLocalite, setSelectedLocalite] = useState(null);
  const [editedLocalite, setEditedLocalite] = useState(null);
  const [localiteToDelete, setLocaliteToDelete] = useState(null);
  const [newLocalite, setNewLocalite] = useState({
    nom: "",
    type_id: "",
    pays_code: userRole === 'super_admin' ? "" : country_code,
    parent_id: "",
    latitude: "",
    longitude: ""
  });

  // États pour les localités
  const [loadingLocalites, setLoadingLocalites] = useState(false);
  const [localitesMap, setLocalitesMap] = useState({});
  
  // Nouveaux états pour la hiérarchie à deux niveaux (AJOUT)
  const [selectedParentType, setSelectedParentType] = useState('');
  const [availableParentTypes, setAvailableParentTypes] = useState([]);
  const [filteredParentsByType, setFilteredParentsByType] = useState([]);
  
  // États pour la recherche de parent (AJOUT)
  const [parentSearchTerm, setParentSearchTerm] = useState('');
  const [showParentDropdown, setShowParentDropdown] = useState(false);
  
  // États pour l'édition - hiérarchie à deux niveaux
  const [editSelectedParentType, setEditSelectedParentType] = useState('');
  const [editAvailableParentTypes, setEditAvailableParentTypes] = useState([]);
  const [editFilteredParentsByType, setEditFilteredParentsByType] = useState([]);
  const [editParentSearchTerm, setEditParentSearchTerm] = useState('');
  const [editShowParentDropdown, setEditShowParentDropdown] = useState(false);
  const [editLoadingParents, setEditLoadingParents] = useState(false);
  
  // États pour les chargements
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingParents, setLoadingParents] = useState(false);
  
  const [typeFilter, setTypeFilter] = useState('tous');

  // États pour les notifications
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });

  // Obtenir le token d'authentification
  const getAuthToken = () => {
    return sessionStorage.getItem("authToken");
  };

  // Headers pour les requêtes API
  const getHeaders = () => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  };

  // Charger les types depuis l'API
  const fetchTypes = async () => {
    try {
      setLoadingTypes(true);
      const response = await axios.get(TYPES_ENDPOINT, {
        headers: getHeaders(),
      });
      
      if (response.data && Array.isArray(response.data)) {
        setTypes(response.data);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setTypes(response.data.data);
      }
    } catch (error) {
      console.error("Erreur chargement types:", error);
      showAlert(`Erreur de chargement des types: ${error.message}`, 'danger');
      setTypes([
        { id: 1, nom: "Commune", code: 1000 },
        { id: 2, nom: "Ville", code: 1001 },
        { id: 3, nom: "Quartier", code: 1002 },
        { id: 4, nom: "Arrondissement", code: 1003 },
        { id: 5, nom: "Département", code: 1004 },
        { id: 6, nom: "Province", code: 1005 }
      ]);
    } finally {
      setLoadingTypes(false);
    }
  };

  // Charger les localités depuis l'API
const fetchLocalites = async () => {
  try {
    setLoading(true);
    setError(null);
    
    const response = await axios.get(LOCALITES_ENDPOINT, {
      headers: getHeaders(),
    });
    
    if (response.data && Array.isArray(response.data)) {
      // S'assurer que chaque localité a un identifiant cohérent
      const formattedData = response.data.map(item => ({
        ...item,
        localite_id: item.localite_id || item.id, // Utiliser localite_id ou id
        id: item.id || item.localite_id
      }));
      setLocalites(formattedData);
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      const formattedData = response.data.data.map(item => ({
        ...item,
        localite_id: item.localite_id || item.id,
        id: item.id || item.localite_id
      }));
      setLocalites(formattedData);
    } else {
      throw new Error('Format de données invalide');
    }
  } catch (err) {
    setError(`${err.response?.data?.message || err.message}`);
    setLocalites(LOCALITES_STATIQUES);
  } finally {
    setLoading(false);
  }
};

  // === FONCTIONS POUR LA HIÉRARCHIE À DEUX NIVEAUX ===
  
const getAvailableParentTypes = (childTypeCode) => {
  if (!childTypeCode) return [];
  // Exclure le type actuel pour éviter l'auto-référence
  return types.filter(t => t.code !== parseInt(childTypeCode));
};


// Charger les parents selon le type sélectionné (pour AJOUT)
const fetchParentsByType = async (typeCode) => {
  console.log('🔍 fetchParentsByType appelé avec typeCode:', typeCode);
  
  if (!typeCode) {
    console.log('⚠️ typeCode vide, réinitialisation');
    setFilteredParentsByType([]);
    return;
  }

  try {
    setLoadingParents(true);
    
    console.log('📡 Appel API vers:', LOCALITES_ENDPOINT);
    console.log('📡 Paramètres:', { type: typeCode }); // ← CHANGÉ: type au lieu de type_code
    
    const response = await axios.get(LOCALITES_ENDPOINT, {
      headers: getHeaders(),
      params: {
        type: typeCode  // ← CHANGÉ: type au lieu de type_code
      }
    });
    
    console.log('📡 Réponse API reçue:', response.data);
    
    let allData = [];
    if (response.data && Array.isArray(response.data)) {
      allData = response.data;
      console.log('✅ Données formatées depuis response.data (array):', allData.length);
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      allData = response.data.data;
      console.log('✅ Données formatées depuis response.data.data (array):', allData.length);
    } else {
      console.warn('⚠️ Format de données inattendu:', response.data);
    }
    
    console.log('📊 Toutes les localités reçues:', allData.length);
    
    // 🔥 IMPORTANT: Le contrôleur filtre déjà par type via le paramètre 'type'
    // Donc on n'a pas besoin de refiltrer ici, mais on garde au cas où
    let parents = allData;
    
    // Filtrer par pays si nécessaire (déjà fait par le contrôleur mais on garde)
    if (userRole !== 'super_admin' && country_code) {
      const beforeCountryFilter = parents.length;
      parents = parents.filter(p => p.pays_code === country_code);
      console.log(`📊 Après filtre par pays ${country_code}:`, parents.length, 'éléments (avant:', beforeCountryFilter, ')');
    }
    
    // Normaliser les identifiants
    const formattedParents = parents.map(p => ({
      ...p,
      localite_id: p.localite_id || p.id,
      id: p.id || p.localite_id
    }));
    
    console.log('✅ Parents formatés:', formattedParents.length);
    console.log('✅ Détails des parents:', formattedParents.map(p => ({ id: p.id, nom: p.nom, type: getTypeName(p.type_code) })));
    
    setFilteredParentsByType(formattedParents);
    console.log(`✅ setFilteredParentsByType mis à jour avec ${formattedParents.length} éléments`);
    
  } catch (error) {
    console.error("❌ Erreur chargement parents par type:", error);
    console.error("❌ Détails de l'erreur:", error.response?.data || error.message);
    showAlert(`Erreur de chargement des parents: ${error.message}`, 'danger');
    setFilteredParentsByType([]);
  } finally {
    setLoadingParents(false);
    console.log('🏁 fetchParentsByType terminé');
  }
};



const fetchEditParentsByType = async (typeCode, currentLocaliteId = null) => {
  console.log('🔍 fetchEditParentsByType appelé avec typeCode:', typeCode);
  
  if (!typeCode) {
    setEditFilteredParentsByType([]);
    return;
  }

  try {
    setEditLoadingParents(true);
    
    console.log('📡 Appel API vers:', LOCALITES_ENDPOINT);
    console.log('📡 Paramètres:', { type: typeCode });
    
    const response = await axios.get(LOCALITES_ENDPOINT, {
      headers: getHeaders(),
      params: {
        type: typeCode
      }
    });
    
    // 🔥 AJOUTER CE LOG POUR VOIR LA RÉPONSE COMPLÈTE
    console.log('📡 Réponse API COMPLÈTE:', JSON.stringify(response.data, null, 2));
    console.log('📡 Type de response.data:', typeof response.data);
    console.log('📡 Est-ce un tableau?', Array.isArray(response.data));
    
    let allData = [];
    if (response.data && Array.isArray(response.data)) {
      allData = response.data;
      console.log('✅ Données depuis response.data (array):', allData.length);
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      allData = response.data.data;
      console.log('✅ Données depuis response.data.data (array):', allData.length);
    } else {
      console.warn('⚠️ Format de données inattendu:', response.data);
      // 🔥 ESSAYER DE RÉCUPÉRER LES DONNÉES AUTREMENT
      if (response.data && typeof response.data === 'object') {
        // Parcourir les propriétés de l'objet pour trouver un tableau
        for (const key in response.data) {
          if (Array.isArray(response.data[key])) {
            allData = response.data[key];
            console.log(`✅ Données trouvées dans response.data.${key}:`, allData.length);
            break;
          }
        }
      }
    }
    
    console.log('📊 allData après extraction:', allData);
    console.log('📊 Nombre de localités:', allData.length);
    
    // Afficher les 5 premiers éléments pour vérification
    if (allData.length > 0) {
      console.log('📊 Exemple des 5 premières localités:', allData.slice(0, 5).map(l => ({ 
        id: l.id || l.localite_id, 
        nom: l.nom, 
        type_code: l.type_code,
        pays_code: l.pays_code
      })));
    }
    
    let parents = allData;
    
    // Filtrer par type_code (au cas où le filtre serveur ne fonctionne pas)
    console.log('📊 Filtrage par type_code:', typeCode);
    const beforeTypeFilter = parents.length;
    parents = parents.filter(p => p.type_code === parseInt(typeCode));
    console.log(`📊 Après filtre type_code ${typeCode}:`, parents.length, '(avant:', beforeTypeFilter, ')');
    
    // Filtrer par pays si nécessaire
    if (userRole !== 'super_admin' && country_code) {
      const beforeCountryFilter = parents.length;
      parents = parents.filter(p => p.pays_code === country_code);
      console.log(`📊 Après filtre pays ${country_code}:`, parents.length, '(avant:', beforeCountryFilter, ')');
    }
    
    // Exclure la localité actuelle si en édition
    if (currentLocaliteId) {
      const beforeExcludeFilter = parents.length;
      parents = parents.filter(p => (p.localite_id || p.id) !== currentLocaliteId);
      console.log(`📊 Après exclusion de ${currentLocaliteId}:`, parents.length, '(avant:', beforeExcludeFilter, ')');
    }
    
    // Normaliser les identifiants
    const formattedParents = parents.map(p => ({
      ...p,
      localite_id: p.localite_id || p.id,
      id: p.id || p.localite_id
    }));
    
    console.log('✅ Parents formatés FINAUX:', formattedParents.length);
    console.log('✅ Détails des parents formatés:', formattedParents.map(p => ({ 
      id: p.id, 
      nom: p.nom, 
      type: p.type_code 
    })));
    
    setEditFilteredParentsByType(formattedParents);
    console.log(`✅ setEditFilteredParentsByType mis à jour avec ${formattedParents.length} éléments`);
    
  } catch (error) {
    console.error("❌ Erreur chargement parents par type:", error);
    console.error("❌ Détails de l'erreur:", error.response?.data || error.message);
    showAlert(`Erreur de chargement des parents: ${error.message}`, 'danger');
    setEditFilteredParentsByType([]);
  } finally {
    setEditLoadingParents(false);
    console.log('🏁 fetchEditParentsByType terminé');
  }
};



  // === FONCTIONS UTILITAIRES ===

  const getTypeName = (typeCode) => {
    if (typeCode === null || typeCode === undefined) return "Pays";
    const typeInfo = types.find(t => t.code === typeCode);
    return typeInfo ? typeInfo.nom : "Inconnu";
  };

  const getTypeColor = (typeNom) => {
    const colors = {
      'Pays': 'primary',
      'Province': 'success',
      'Département': 'info',
      'Commune': 'warning',
      'Ville': 'secondary',
      'Arrondissement': 'danger',
      'Quartier': 'dark'
    };
    return colors[typeNom] || 'primary';
  };

  const getTypeBadge = (typeCode) => {
    const typeName = getTypeName(typeCode);
    const color = getTypeColor(typeName);
    
    return (
      <Badge bg={color} className="px-3 py-2 rounded-pill">
        {typeName}
      </Badge>
    );
  };

  const getParentName = (localite) => {
    if (!localite.parent_id) return "-";
    
    if (localite.parent && localite.parent.nom) {
      return localite.parent.nom;
    }
    
    const parent = localites.find(l => l.localite_id === localite.parent_id);
    return parent ? parent.nom : "Inconnu";
  };

  const getParentNameById = (parentId) => {
    if (!parentId) return "-";
    const parent = localites.find(l => l.localite_id === parentId);
    return parent ? parent.nom : "Inconnu";
  };

  const showAlert = (message, type = 'info', duration = 4000) => {
    setAlertMessage({ show: true, message, type });
    if (duration > 0) {
      setTimeout(() => {
        setAlertMessage(prev => ({ ...prev, show: false }));
      }, duration);
    }
  };

  const hideAlert = () => {
    setAlertMessage(prev => ({ ...prev, show: false }));
  };

  // Filtrage et tri des données
  const filteredAndSortedItems = React.useMemo(() => {
    let filtered = [...localites];
    
    if (filterText) {
      const searchTerm = filterText.toLowerCase();
      filtered = filtered.filter(item => 
        (item.nom && item.nom.toLowerCase().includes(searchTerm)) ||
        getTypeName(item.type_code).toLowerCase().includes(searchTerm)
      );
    }

    if (typeFilter !== 'tous') {
      filtered = filtered.filter(item => item.type_code === parseInt(typeFilter));
    }

    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (sortField === 'nom') {
        aValue = a.nom || '';
        bValue = b.nom || '';
      } else if (sortField === 'type_nom') {
        aValue = getTypeName(a.type_code) || '';
        bValue = getTypeName(b.type_code) || '';
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [localites, filterText, typeFilter, sortField, sortDirection]);

  // Statistiques
  const stats = React.useMemo(() => {
    const total = localites.length;
    const byType = {
      Province: localites.filter(l => getTypeName(l.type_code) === 'Province').length,
      Département: localites.filter(l => getTypeName(l.type_code) === 'Département').length,
      Commune: localites.filter(l => getTypeName(l.type_code) === 'Commune').length,
      Ville: localites.filter(l => getTypeName(l.type_code) === 'Ville').length,
      Arrondissement: localites.filter(l => getTypeName(l.type_code) === 'Arrondissement').length,
      Quartier: localites.filter(l => getTypeName(l.type_code) === 'Quartier').length,
      Pays: localites.filter(l => l.type_code === null).length
    };
    
    return { total, ...byType };
  }, [localites]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Gestion des sélections
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedLocalites(currentItems.map(item => item.localite_id));
    } else {
      setSelectedLocalites([]);
    }
  };

  const handleSelectLocalite = (localiteId) => {
    setSelectedLocalites(prev => 
      prev.includes(localiteId) 
        ? prev.filter(id => id !== localiteId)
        : [...prev, localiteId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedLocalites.length === currentItems.length;
  const isSomeSelected = selectedLocalites.length > 0 && selectedLocalites.length < currentItems.length;

  const formatCoordinates = (lat, lng) => {
    if (!lat || !lng) return "-";
    return `${parseFloat(lat).toFixed(4)}, ${parseFloat(lng).toFixed(4)}`;
  };

  // Gestion de la suppression
// Gestion de la suppression - Corrigé
const handleDeleteLocalite = (localite) => {
  setLocaliteToDelete(localite);
  setShowDeleteModal(true);
};

const handleConfirmDelete = async () => {
  if (!localiteToDelete) return;

  try {
    setLoadingDelete(true);
    
    // Utiliser l'identifiant correct (localite_id ou id)
    const deleteId = localiteToDelete.localite_id || localiteToDelete.id;
    
    await axios.delete(`${LOCALITES_ENDPOINT}/${deleteId}`, {
      headers: getHeaders(),
    });

    setLocalites(prev => prev.filter(l => (l.localite_id || l.id) !== deleteId));
    setSelectedLocalites(prev => prev.filter(id => id !== deleteId));
    showAlert('Localité supprimée avec succès', 'success');
  } catch (error) {
    console.error("Erreur suppression:", error);
    showAlert(`Erreur lors de la suppression: ${error.message}`, 'danger');
  } finally {
    setLoadingDelete(false);
    setShowDeleteModal(false);
    setLocaliteToDelete(null);
  }
};

// Gestion de la modification - Corrigé
const handleEditLocalite = (localite) => {
  setSelectedLocalite(localite);
  
  const localiteId = localite.localite_id || localite.id;
  
  let parentTypeCode = '';
  let parentLocalite = null;
  
  if (localite.parent_id) {
    parentLocalite = localites.find(l => (l.localite_id || l.id) === localite.parent_id);
    if (parentLocalite) {
      parentTypeCode = parentLocalite.type_code;
    }
  }
  
  setEditedLocalite({
    localite_id: localiteId,
    id: localite.id || localiteId,
    nom: localite.nom,
    type_id: localite.type_code,
    parent_id: localite.parent_id || "",
    parent_type_id: parentTypeCode,
    latitude: localite.latitude || "",
    longitude: localite.longitude || ""
  });
  
  const availableTypes = getAvailableParentTypes(localite.type_code);
  setEditAvailableParentTypes(availableTypes);
  
  if (parentTypeCode) {
    setEditSelectedParentType(parentTypeCode.toString());
    setEditParentSearchTerm(parentLocalite ? parentLocalite.nom : '');
    fetchEditParentsByType(parentTypeCode, localiteId);
  } else {
    setEditSelectedParentType('');
    setEditParentSearchTerm('');
    setEditFilteredParentsByType([]);
  }
  
  setShowEditModal(true);
};

const handleSaveEdit = async () => {
  if (!editedLocalite) return;

  try {
    setLoadingUpdate(true);
    
    const payload = {
      nom: editedLocalite.nom,
      type_code: parseInt(editedLocalite.type_id),
      pays_code: country_code,
      parent_id: editedLocalite.parent_id || null,
      latitude: editedLocalite.latitude ? parseFloat(editedLocalite.latitude) : 0,
      longitude: editedLocalite.longitude ? parseFloat(editedLocalite.longitude) : 0
    };
    
    const updateId = editedLocalite.localite_id || editedLocalite.id;
    
    await axios.put(
      `${LOCALITES_ENDPOINT}/${updateId}`,
      payload,
      { headers: getHeaders() }
    );

    await fetchLocalites();
    
    setShowEditModal(false);
    showAlert('Localité modifiée avec succès', 'success');
  } catch (error) {
    console.error("Erreur modification:", error);
    showAlert(`Erreur lors de la modification: ${error.message}`, 'danger');
  } finally {
    setLoadingUpdate(false);
  }
};

  const handleEditTypeChange = (e) => {
    const typeCode = e.target.value;
    setEditedLocalite(prev => ({
      ...prev,
      type_id: typeCode,
      parent_id: "",
      parent_type_id: ""
    }));
    
    setEditSelectedParentType('');
    setEditFilteredParentsByType([]);
    setEditParentSearchTerm('');
    
    const availableTypes = getAvailableParentTypes(typeCode);
    setEditAvailableParentTypes(availableTypes);
  };

  const handleEditParentTypeChange = (e) => {
    const parentTypeCode = e.target.value;
    setEditSelectedParentType(parentTypeCode);
    setEditParentSearchTerm('');
    setEditedLocalite(prev => ({
      ...prev,
      parent_id: "",
      parent_type_id: parentTypeCode
    }));
    
    if (parentTypeCode) {
      fetchEditParentsByType(parentTypeCode, editedLocalite?.id);
    } else {
      setEditFilteredParentsByType([]);
    }
  };

  const handleEditSelectParent = (parentLocaliteId, parentNom) => {
    setEditedLocalite(prev => ({
      ...prev,
      parent_id: parentLocaliteId
    }));
    setEditParentSearchTerm(parentNom);
    setEditShowParentDropdown(false);
  };



  // Gestion de l'affichage des détails
  const handleViewDetails = (localite) => {
    setSelectedLocalite(localite);
    setShowDetailsModal(true);
  };

  // Gestion de l'ajout
  const handleAddLocalite = async () => {
    if (!newLocalite.nom.trim()) {
      showAlert('Le nom de la localité est requis', 'warning');
      return;
    }

    if (!newLocalite.type_id) {
      showAlert('Veuillez sélectionner un type', 'warning');
      return;
    }

    if (userRole === 'super_admin' && !newLocalite.pays_code?.trim()) {
      showAlert('Le code pays est requis', 'warning');
      return;
    }

    try {
      setLoadingAdd(true);
      
      const selectedType = types.find(t => t.code === parseInt(newLocalite.type_id));
      if (!selectedType) {
        showAlert('Type de localité invalide', 'warning');
        return;
      }

      const paysCodeValue = userRole === 'super_admin' 
        ? newLocalite.pays_code?.toUpperCase() 
        : country_code;
      
      const payload = {
        nom: newLocalite.nom,
        pays_code: paysCodeValue,
        parent_id: newLocalite.parent_id || null,
        latitude: newLocalite.latitude ? parseFloat(newLocalite.latitude) : 0,
        longitude: newLocalite.longitude ? parseFloat(newLocalite.longitude) : 0,
        type_code: parseInt(newLocalite.type_id)
      };
      
      const response = await axios.post(
        `${LOCALITES_ENDPOINT}/add`,
        payload,
        { headers: getHeaders() }
      );

      await fetchLocalites();
      
      setShowAddModal(false);
      setNewLocalite({
        nom: "",
        type_id: "",
        pays_code: userRole === 'super_admin' ? "" : country_code,
        parent_id: "",
        latitude: "",
        longitude: ""
      });
      setSelectedParentType('');
      setAvailableParentTypes([]);
      setFilteredParentsByType([]);
      setParentSearchTerm('');

      showAlert('Localité ajoutée avec succès', 'success');
    } catch (error) {
      console.error("Erreur ajout:", error);
      
      let errorMessage = `Erreur lors de l'ajout: ${error.message}`;
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      showAlert(errorMessage, 'danger');
    } finally {
      setLoadingAdd(false);
    }
  };

  // Gestionnaire de changement de type pour AJOUT
// Gestionnaire de changement de type pour AJOUT
const handleTypeChange = (e) => {
  const typeCode = e.target.value;
  console.log('🔄 handleTypeChange - type sélectionné:', typeCode);
  
  setNewLocalite(prev => ({
    ...prev,
    type_id: typeCode,
    parent_id: ""
  }));
  
  setSelectedParentType('');
  setFilteredParentsByType([]);
  setParentSearchTerm('');
  
  const availableTypes = getAvailableParentTypes(typeCode);
  console.log('📋 Types parents disponibles:', availableTypes.map(t => ({ code: t.code, nom: t.nom })));
  setAvailableParentTypes(availableTypes);
};

const handleParentTypeChange = (e) => {
  const parentTypeCode = e.target.value;
  console.log('🔄 handleParentTypeChange - type sélectionné:', parentTypeCode);
  
  setSelectedParentType(parentTypeCode);
  setParentSearchTerm('');
  setNewLocalite(prev => ({
    ...prev,
    parent_id: ""
  }));
  
  if (parentTypeCode) {
    // Réinitialiser avant de charger
    setFilteredParentsByType([]);
    setShowParentDropdown(false);
    console.log('📡 Appel de fetchParentsByType avec:', parentTypeCode);
    // Charger les parents du type sélectionné
    fetchParentsByType(parentTypeCode);
  } else {
    console.log('⚠️ Aucun type sélectionné, réinitialisation');
    setFilteredParentsByType([]);
    setShowParentDropdown(false);
  }
};





const handleSelectParent = (parentLocaliteId, parentNom) => {
  console.log('🔄 handleSelectParent - ID:', parentLocaliteId, 'Nom:', parentNom);
  setNewLocalite(prev => ({
    ...prev,
    parent_id: parentLocaliteId
  }));
  setParentSearchTerm(parentNom);
  setShowParentDropdown(false);
  console.log('✅ Parent sélectionné:', { parentLocaliteId, parentNom });
};

  const SortableHeader = ({ field, children }) => (
    <th 
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => handleSort(field)}
      className="position-relative"
    >
      {children}
      {sortField === field && (
        <span className="ms-1">
          {sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      )}
    </th>
  );

  const handleRefresh = () => {
    fetchTypes();
    fetchLocalites();
  };

  useEffect(() => {
    fetchTypes();
    fetchLocalites();
  }, []);

  return (
    <React.Fragment>
      <Header />
      
      <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
        <Toast show={alertMessage.show} onClose={hideAlert} bg={alertMessage.type} delay={4000} autohide>
          <Toast.Header closeButton={false}>
            {alertMessage.type === 'success' && <CheckCircle className="me-2 text-success" />}
            {alertMessage.type === 'danger' && <XCircle className="me-2 text-danger" />}
            {alertMessage.type === 'warning' && <ExclamationCircle className="me-2 text-warning" />}
            {alertMessage.type === 'info' && <CheckCircle className="me-2 text-info" />}
            <strong className="me-auto">Notification</strong>
            <small>maintenant</small>
          </Toast.Header>
          <Toast.Body className="text-white fw-medium">
            {alertMessage.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>
      
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <div className="container-fluid">
          {/* En-tête */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <div className="mb-3 mb-md-0">
                  <ol className="breadcrumb fs-sm mb-1">
                    <li className="breadcrumb-item"><Link to="/admin/dashboard">Tableau de bord</Link></li>
                    <li className="breadcrumb-item active" aria-current="page">Localités</li>
                  </ol>
                  <h4 className="main-title mb-0 d-flex align-items-center">
                    <Diagram3 className="me-2 text-primary" size={28} />
                    Gestion des Localités
                  </h4>
                  <p className="text-muted mb-0">Gérez la hiérarchie complète</p>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  {error && (
                    <Button variant="warning" size="sm" onClick={handleRefresh} className="d-flex align-items-center">
                      <ExclamationCircle className="me-2" />Réessayer
                    </Button>
                  )}
                  
                  {selectedLocalites.length > 0 && (
                    <Button variant="outline-danger" size="sm" onClick={() => {
                      const toDelete = localites.filter(l => selectedLocalites.includes(l.localite_id));
                      if (toDelete.length > 0) {
                        setLocaliteToDelete(toDelete[0]);
                        setShowDeleteModal(true);
                      }
                    }}>
                      <Trash className="me-2" size={16} />Supprimer ({selectedLocalites.length})
                    </Button>
                  )}
                  
                  <Button variant="success" onClick={() => setShowAddModal(true)} className="d-flex align-items-center shadow-sm" disabled={loadingTypes}>
                    {loadingTypes ? (
                      <><Spinner size="sm" className="me-2" />Chargement...</>
                    ) : (
                      <><PlusLg className="me-2" size={16} />Nouvelle Localité</>
                    )}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Cartes de statistiques */}
          {/* <Row className="g-3 mb-4">
            <Col md={3}>
              <Card className="border-0 shadow-sm rounded-3 h-100 stats-card">
                <div className="card-gradient bg-primary"></div>
                <Card.Body className="d-flex align-items-center">
                  <div className="bg-primary bg-opacity-10 p-3 rounded-3 me-3">
                    <Globe size={28} className="text-primary" />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Total Localités</h6>
                    <h3 className="mb-0 fw-bold">{stats.total}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm rounded-3 h-100 stats-card">
                <div className="card-gradient bg-info"></div>
                <Card.Body className="d-flex align-items-center">
                  <div className="bg-info bg-opacity-10 p-3 rounded-3 me-3">
                    <Building size={28} className="text-info" />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Provinces & Départements</h6>
                    <h3 className="mb-0 fw-bold">{(stats.Province || 0) + (stats.Département || 0)}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm rounded-3 h-100 stats-card">
                <div className="card-gradient bg-success"></div>
                <Card.Body className="d-flex align-items-center">
                  <div className="bg-success bg-opacity-10 p-3 rounded-3 me-3">
                    <PinMap size={28} className="text-success" />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Villes & Communes</h6>
                    <h3 className="mb-0 fw-bold">{(stats.Ville || 0) + (stats.Commune || 0)}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm rounded-3 h-100 stats-card">
                <div className="card-gradient bg-warning"></div>
                <Card.Body className="d-flex align-items-center">
                  <div className="bg-warning bg-opacity-10 p-3 rounded-3 me-3">
                    <House size={28} className="text-warning" />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Quartiers</h6>
                    <h3 className="mb-0 fw-bold">{stats.Quartier || 0}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row> */}

          {/* Barre de recherche et filtres */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm rounded-3">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <InputGroup>
                        <InputGroup.Text className="bg-light border-end-0">
                          <Search className="text-primary" />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Rechercher par nom..."
                          value={filterText}
                          onChange={(e) => setFilterText(e.target.value)}
                          className="border-start-0 bg-light"
                        />
                        {filterText && (
                          <Button variant="outline-secondary" className="border-start-0" onClick={() => setFilterText('')}>
                            <X size={16} />
                          </Button>
                        )}
                      </InputGroup>
                    </Col>
                    <Col md={3}>
                      <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-light">
                        <option value="tous">Tous les types</option>
                        {types.map(type => (
                          <option key={type.code} value={type.code}>{type.nom}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Select value={itemsPerPage} onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }} className="bg-light">
                        <option value="5">5 / page</option>
                        <option value="10">10 / page</option>
                        <option value="25">25 / page</option>
                        <option value="50">50 / page</option>
                      </Form.Select>
                    </Col>
                    <Col md={1}>
                      <Button variant="outline-primary" onClick={handleRefresh} className="w-100 d-flex align-items-center justify-content-center" disabled={loading}>
                        <Cloud size={16} />
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tableau principal */}
          <Row>
            <Col md={12}>
              <Card className="shadow-sm border-0 rounded-3">
                <Card.Header className="bg-primary d-flex justify-content-between align-items-center py-3 border-bottom">
                  <div className="d-flex align-items-center">
                    <Diagram3 className="me-2 text-white" />
                    <span className="fw-semibold text-white">Liste des Localités ({filteredAndSortedItems.length})</span>
                    {filterText && <Badge bg="info" className="ms-2">Filtre actif</Badge>}
                  </div>
                  <div className="text-white small d-flex align-items-center">
                    {selectedLocalites.length > 0 && <span className="me-3">{selectedLocalites.length} sélectionné(s)</span>}
                  </div>
                </Card.Header>
                
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              style={{ borderRadius: '50%', width: '18px', height: '18px', cursor: 'pointer' }}
                              checked={isAllSelected}
                              ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th>ID</th>
                          <th>Code</th>
                          <SortableHeader field="type_nom">Type</SortableHeader>
                          <SortableHeader field="nom">Nom</SortableHeader>
                          <th>Parent</th>
                          <th>Coordonnées</th>
                          <th style={{ width: '120px' }} className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading && localites.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-5">
                              <Spinner animation="border" variant="primary" className="me-3" />
                              <span className="text-muted">Chargement des localités...</span>
                            </td>
                          </tr>
                        ) : error && localites.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-5">
                              <ExclamationCircle size={48} className="text-danger mb-3" />
                              <h5>Erreur de chargement</h5>
                              <p>{error}</p>
                              <Button variant="primary" onClick={handleRefresh} className="mt-3">Réessayer</Button>
                            </td>
                          </tr>
                        ) : currentItems.length > 0 ? (
                          currentItems.map((localite) => (
                            <tr key={localite.localite_id}>
                              <td>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  style={{ borderRadius: '50%', width: '18px', height: '18px', cursor: 'pointer' }}
                                  checked={selectedLocalites.includes(localite.localite_id)}
                                  onChange={() => handleSelectLocalite(localite.localite_id)}
                                />
                              </td>
                              <td><Badge bg="light" text="dark" className="fw-semibold">{localite.id}</Badge></td>
                              <td><code className="text-primary" style={{ fontSize: '11px' }}>{localite.localite_id}</code></td>
                              <td>{getTypeBadge(localite.type_code)}</td>
                              <td><strong>{localite.nom}</strong></td>
                              <td>
                                {localite.parent_id ? (
                                  <Badge bg="secondary" className="px-2 py-1 rounded-pill">
                                    <GeoAlt className="me-1" size={10} />{getParentName(localite)}
                                  </Badge>
                                ) : <span className="text-muted">-</span>}
                              </td>
                              <td>
                                {localite.latitude && localite.longitude ? (
                                  <small className="text-muted">{parseFloat(localite.latitude).toFixed(4)}, {parseFloat(localite.longitude).toFixed(4)}</small>
                                ) : <span className="text-muted">-</span>}
                              </td>
                              <td>
                                <div className="d-flex gap-1 justify-content-center">
                                  <Button size="sm" variant="outline-primary" title="Voir détails" onClick={() => handleViewDetails(localite)} className="rounded-circle" style={{ width: '34px', height: '34px', padding: 0 }}>
                                    <Eye size={14} />
                                  </Button>
                                  <Button size="sm" variant="outline-warning" title="Modifier" onClick={() => handleEditLocalite(localite)} className="rounded-circle" style={{ width: '34px', height: '34px', padding: 0 }}>
                                    <Pencil size={14} />
                                  </Button>
                                  <Button size="sm" variant="outline-danger" title="Supprimer" onClick={() => handleDeleteLocalite(localite)} className="rounded-circle" style={{ width: '34px', height: '34px', padding: 0 }}>
                                    <Trash size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-5">
                              <Diagram3 size={48} className="text-muted opacity-25" />
                              <h5 className="mt-3 text-muted">AUCUNE LOCALITÉ TROUVÉE</h5>
                              {filterText && (
                                <Button variant="outline-primary" size="sm" onClick={() => setFilterText('')}>
                                  Réinitialiser la recherche
                                </Button>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {totalPages > 1 && (
                    <div className="p-3 border-top d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
                      <div className="text-muted small">
                        Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong> localités
                        {selectedLocalites.length > 0 && <span className="ms-2 text-primary">• {selectedLocalites.length} sélectionné(s)</span>}
                      </div>
                      <Pagination className="mb-0">
                        <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} />
                        {[...Array(Math.min(totalPages, 7))].map((_, index) => {
                          const pageNum = index + 1;
                          return (
                            <Pagination.Item key={pageNum} active={pageNum === currentPage} onClick={() => setCurrentPage(pageNum)} className={pageNum === currentPage ? 'fw-bold' : ''}>
                              {pageNum}
                            </Pagination.Item>
                          );
                        })}
                        {totalPages > 7 && (
                          <>
                            <Pagination.Ellipsis />
                            <Pagination.Item onClick={() => setCurrentPage(totalPages)}>{totalPages}</Pagination.Item>
                          </>
                        )}
                        <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} />
                      </Pagination>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

{/* ===== MODAL D'AJOUT ===== */}
<Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered size="lg">
  <Modal.Header closeButton className="text-white" style={{ background: "linear-gradient(135deg, #198754, #157347)" }}>
    <Modal.Title className="d-flex align-items-center">
      <PlusLg className="me-2" />Nouvelle Localité
    </Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <Form>
      {/* Supprimer la condition userRole === 'super_admin' et afficher le même formulaire pour tous */}
      <Row className="g-3">
        {/* Code pays - visible pour tous */}
        <Col md={12}>
          <FloatingLabel label="Code pays *" className="mb-3">
            <Form.Control
              type="text"
              placeholder="Ex: GA, FR, CM..."
              value={newLocalite.pays_code}
              onChange={(e) => setNewLocalite({ ...newLocalite, pays_code: e.target.value.toUpperCase() })}
              required
            />
            <Form.Text className="text-muted">Code ISO du pays (2 lettres)</Form.Text>
          </FloatingLabel>
        </Col>

        {/* Type de localité - visible pour tous */}
        <Col md={6}>
          <FloatingLabel label="Type de localité *" className="mb-3">
            <Form.Select 
              value={newLocalite.type_id} 
              onChange={handleTypeChange} 
              required 
              disabled={loadingTypes}
            >
              <option value="">Sélectionner un type</option>
              {types.map(type => (
                <option key={type.code} value={type.code}>{type.nom}</option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </Col>

        {/* Nom de la localité - visible pour tous */}
        <Col md={6}>
          <FloatingLabel label="Nom de la localité *" className="mb-3">
            <Form.Control
              type="text"
              placeholder="Nom"
              value={newLocalite.nom}
              onChange={(e) => setNewLocalite({ ...newLocalite, nom: e.target.value })}
              required
            />
          </FloatingLabel>
        </Col>

        {/* Type de parent - visible pour tous */}
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold mb-2">
              Type de parent <span className="text-muted small">(optionnel)</span>
              {loadingParents && <Spinner size="sm" className="ms-2" />}
            </Form.Label>
            <Form.Select
              value={selectedParentType}
              onChange={handleParentTypeChange}
              disabled={!newLocalite.type_id || availableParentTypes.length === 0}
              className="bg-light"
            >
              <option value="">-- Aucun parent --</option>
              {availableParentTypes.map(type => (
                <option key={type.code} value={type.code}>{type.nom}</option>
              ))}
            </Form.Select>
            {!newLocalite.type_id && (
              <Form.Text className="text-muted">
                Sélectionnez d'abord un type pour voir les parents disponibles
              </Form.Text>
            )}
          </Form.Group>
        </Col>

        {/* Sélection du parent - visible pour tous */}
        {selectedParentType && (
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold mb-2">
                Sélectionner le parent 
                {loadingParents && <Spinner size="sm" className="ms-2" />}
                <span className="text-muted small ms-2">
                  ({filteredParentsByType.length} disponible(s))
                </span>
              </Form.Label>
              <div className="position-relative">
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <SearchIcon size={16} className="text-primary" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder={loadingParents ? "Chargement..." : "Rechercher un parent..."}
                    value={parentSearchTerm}
                    onChange={(e) => {
                      setParentSearchTerm(e.target.value);
                      setShowParentDropdown(true);
                    }}
                    onFocus={() => {
                      if (filteredParentsByType.length > 0) {
                        setShowParentDropdown(true);
                      }
                    }}
                    disabled={loadingParents || filteredParentsByType.length === 0}
                    className="border-start-0 bg-light"
                  />
                  {parentSearchTerm && (
                    <Button 
                      variant="outline-secondary" 
                      className="border-start-0"
                      onClick={() => {
                        setParentSearchTerm('');
                        setNewLocalite(prev => ({ ...prev, parent_id: "" }));
                      }}
                    >
                      <X size={16} />
                    </Button>
                  )}
                </InputGroup>
                
                {showParentDropdown && !loadingParents && filteredParentsByType.length > 0 && (
                  <div 
                    className="position-absolute w-100 mt-1 bg-white rounded-3 shadow-lg border" 
                    style={{ 
                      zIndex: 1050, 
                      maxHeight: '250px', 
                      overflowY: 'auto',
                      top: '100%',
                      left: 0
                    }}
                  >
                    {filteredParentsByType
                      .filter(parent => 
                        parentSearchTerm === '' || 
                        parent.nom?.toLowerCase().includes(parentSearchTerm.toLowerCase())
                      )
                      .map(parent => (
                        <div 
                          key={parent.id || parent.localite_id} 
                          className="p-2 border-bottom hover-bg-light cursor-pointer d-flex justify-content-between align-items-center"
                          onClick={() => {
                            handleSelectParent(parent.localite_id || parent.id, parent.nom);
                            setShowParentDropdown(false);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <span className="fw-medium">{parent.nom}</span>
                          <div>
                            <Badge bg="secondary" className="me-1">{getTypeName(parent.type_code)}</Badge>
                            {parent.code_marche && (
                              <Badge bg="info" className="me-1">{parent.code_marche}</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    {filteredParentsByType.filter(parent => 
                      parentSearchTerm === '' || 
                      parent.nom?.toLowerCase().includes(parentSearchTerm.toLowerCase())
                    ).length === 0 && (
                      <div className="p-3 text-center text-muted">
                        <InfoCircle className="me-2" />
                        Aucun parent trouvé pour "{parentSearchTerm}"
                      </div>
                    )}
                  </div>
                )}
                
                {filteredParentsByType.length === 0 && selectedParentType && !loadingParents && (
                  <Form.Text className="text-warning">
                    <ExclamationCircle className="me-1" size={12} />
                    Aucune localité de type "{getTypeName(selectedParentType)}" disponible
                  </Form.Text>
                )}
              </div>
            </Form.Group>
          </Col>
        )}

        {/* Latitude et Longitude - visibles pour tous */}
        <Col md={6}>
          <FloatingLabel label="Latitude (optionnel)" className="mb-3">
            <Form.Control 
              type="number" 
              step="any" 
              placeholder="Ex: 0.4162" 
              value={newLocalite.latitude} 
              onChange={(e) => setNewLocalite({ ...newLocalite, latitude: e.target.value })} 
            />
          </FloatingLabel>
        </Col>
        <Col md={6}>
          <FloatingLabel label="Longitude (optionnel)" className="mb-3">
            <Form.Control 
              type="number" 
              step="any" 
              placeholder="Ex: 9.4671" 
              value={newLocalite.longitude} 
              onChange={(e) => setNewLocalite({ ...newLocalite, longitude: e.target.value })} 
            />
          </FloatingLabel>
        </Col>
      </Row>
    </Form>
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={loadingAdd}>Annuler</Button>
    <Button variant="success" onClick={handleAddLocalite} disabled={loadingAdd || !newLocalite.nom.trim() || !newLocalite.type_id}>
      {loadingAdd ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : <><Check className="me-2" />Enregistrer</>}
    </Button>
  </Modal.Footer>
</Modal>





        {/* ===== MODAL D'ÉDITION ===== */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
          <Modal.Header closeButton className="text-white" style={{ background: "linear-gradient(135deg, #0dcaf0, #0b5ed7)" }}>
            <Modal.Title className="d-flex align-items-center">
              <Pencil size={20} className="me-2" />Modifier la localité
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4 py-3">
            {editedLocalite && (
              <Form>
                <Row className="g-4">
                  <Col md={6}>
                    <FloatingLabel label="Type *" className="mb-3">
                      <Form.Select value={editedLocalite.type_id} onChange={handleEditTypeChange} required disabled={loadingTypes}>
                        {types.map(type => (
                          <option key={type.code} value={type.code}>{type.nom}</option>
                        ))}
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel label="Nom *" className="mb-3">
                      <Form.Control type="text" placeholder="Nom" value={editedLocalite.nom} onChange={(e) => setEditedLocalite({ ...editedLocalite, nom: e.target.value })} required />
                    </FloatingLabel>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold mb-2">Type de parent <span className="text-muted small">(optionnel)</span></Form.Label>
                      <Form.Select value={editSelectedParentType} onChange={handleEditParentTypeChange} disabled={!editedLocalite.type_id || editAvailableParentTypes.length === 0} className="bg-light">
                        <option value="">-- Aucun parent --</option>
                        {editAvailableParentTypes.map(type => (
                          <option key={type.code} value={type.code}>{type.nom}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                 {editSelectedParentType && (
  <Col md={12}>
    <Form.Group className="mb-3">
      <Form.Label className="fw-semibold mb-2">
        Sélectionner le parent
        {editLoadingParents && <Spinner size="sm" className="ms-2" />}
        <span className="text-muted small ms-2">
          ({editFilteredParentsByType.length} disponible(s))
        </span>
      </Form.Label>
      <div className="position-relative">
        <InputGroup>
          <InputGroup.Text className="bg-light border-end-0">
            <SearchIcon size={16} className="text-primary" />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder={editLoadingParents ? "Chargement..." : "Rechercher un parent..."}
            value={editParentSearchTerm}
            onChange={(e) => {
              console.log('🔍 Édition - Recherche parent:', e.target.value);
              setEditParentSearchTerm(e.target.value);
              setEditShowParentDropdown(true);
            }}
            onFocus={() => {
              console.log('🔍 Édition - Focus, editFilteredParentsByType:', editFilteredParentsByType.length);
              if (editFilteredParentsByType.length > 0) {
                setEditShowParentDropdown(true);
              }
            }}
            disabled={editLoadingParents || editFilteredParentsByType.length === 0}
            className="border-start-0 bg-light"
          />
          {editParentSearchTerm && (
            <Button 
              variant="outline-secondary" 
              className="border-start-0"
              onClick={() => {
                setEditParentSearchTerm('');
                setEditedLocalite(prev => ({ ...prev, parent_id: "" }));
              }}
            >
              <X size={16} />
            </Button>
          )}
        </InputGroup>
        
        {console.log('🎯 État du dropdown:', { 
          editShowParentDropdown, 
          editLoadingParents, 
          editFilteredParentsByTypeLength: editFilteredParentsByType.length 
        })}
        
        {editShowParentDropdown && !editLoadingParents && editFilteredParentsByType.length > 0 && (
          <div 
            className="position-absolute w-100 mt-1 bg-white rounded-3 shadow-lg border" 
            style={{ 
              zIndex: 1060, 
              maxHeight: '250px', 
              overflowY: 'auto',
              top: '100%',
              left: 0
            }}
          >
            {console.log('📋 Édition - Affichage du dropdown avec', editFilteredParentsByType.length, 'parents')}
            {editFilteredParentsByType
              .filter(parent => {
                const match = editParentSearchTerm === '' || parent.nom?.toLowerCase().includes(editParentSearchTerm.toLowerCase());
                return match;
              })
              .map(parent => (
                <div 
                  key={parent.id || parent.localite_id} 
                  className="p-2 border-bottom hover-bg-light cursor-pointer d-flex justify-content-between align-items-center"
                  onClick={() => {
                    console.log('🖱️ Édition - Clic sur le parent:', parent.nom, parent.id);
                    handleEditSelectParent(parent.localite_id || parent.id, parent.nom);
                    setEditShowParentDropdown(false);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="fw-medium">{parent.nom}</span>
                  <Badge bg="secondary">{getTypeName(parent.type_code)}</Badge>
                </div>
              ))}
            {editFilteredParentsByType.filter(parent => 
              editParentSearchTerm === '' || 
              parent.nom?.toLowerCase().includes(editParentSearchTerm.toLowerCase())
            ).length === 0 && (
              <div className="p-3 text-center text-muted">
                <InfoCircle className="me-2" />
                Aucun parent trouvé pour "{editParentSearchTerm}"
              </div>
            )}
          </div>
        )}
        
        {editFilteredParentsByType.length === 0 && editSelectedParentType && !editLoadingParents && (
          <Form.Text className="text-warning">
            <ExclamationCircle className="me-1" size={12} />
            Aucune localité de type "{getTypeName(editSelectedParentType)}" disponible
            <br />
            <small>Vérifiez qu'il existe des localités de ce type dans la base de données.</small>
          </Form.Text>
        )}
      </div>
    </Form.Group>
  </Col>
)}
                  <Col md={6}>
                    <FloatingLabel label="Latitude (optionnel)" className="mb-3">
                      <Form.Control type="number" step="any" placeholder="Latitude" value={editedLocalite.latitude || ''} onChange={(e) => setEditedLocalite({ ...editedLocalite, latitude: e.target.value })} />
                    </FloatingLabel>
                  </Col>
                  <Col md={6}>
                    <FloatingLabel label="Longitude (optionnel)" className="mb-3">
                      <Form.Control type="number" step="any" placeholder="Longitude" value={editedLocalite.longitude || ''} onChange={(e) => setEditedLocalite({ ...editedLocalite, longitude: e.target.value })} />
                    </FloatingLabel>
                  </Col>
                </Row>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer className="px-4">
            <Button variant="outline-secondary" onClick={() => setShowEditModal(false)} disabled={loadingUpdate}>Annuler</Button>
            <Button variant="info" className="text-white" onClick={handleSaveEdit} disabled={loadingUpdate || !(editedLocalite?.nom)?.trim()}>
              {loadingUpdate ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : <><Check size={18} className="me-2" />Enregistrer</>}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ===== MODAL DE DÉTAILS ===== */}
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered size="lg">
          <Modal.Header closeButton className="text-white" style={{ background: "linear-gradient(135deg, #0d6efd, #0a58ca)" }}>
            <Modal.Title className="d-flex align-items-center"><Eye className="me-2" />Détails de la localité</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedLocalite && (
              <div className="p-3">
                <Row className="mb-4">
                  <Col md={12} className="text-center">
                    {getTypeBadge(selectedLocalite.type_code)}
                    <h3 className="mt-3 mb-0">{selectedLocalite.nom}</h3>
                  </Col>
                </Row>
                <Row className="g-3">
                  <Col md={12}>
                    <Card className="border-0 bg-light">
                      <Card.Body>
                        <h6 className="text-muted mb-2">Informations générales</h6>
                        <ListGroup variant="flush">
                          <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between"><span>ID :</span><strong>{selectedLocalite.id}</strong></ListGroup.Item>
                          <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between"><span>Type :</span><strong>{getTypeName(selectedLocalite.type_code)}</strong></ListGroup.Item>
                          <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between"><span>Pays :</span><strong>{selectedLocalite.pays_code || '-'}</strong></ListGroup.Item>
                          <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between"><span>Parent :</span><strong>{getParentName(selectedLocalite)}</strong></ListGroup.Item>
                          {selectedLocalite.latitude && selectedLocalite.longitude && (
                            <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between"><span>Coordonnées :</span><strong>{formatCoordinates(selectedLocalite.latitude, selectedLocalite.longitude)}</strong></ListGroup.Item>
                          )}
                        </ListGroup>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>Fermer</Button>
          </Modal.Footer>
        </Modal>

        {/* ===== MODAL DE SUPPRESSION ===== */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title><ExclamationCircle className="text-warning me-2" />Confirmer la suppression</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <Trash className="display-4 text-danger mb-3" size={48} />
              <h5>Êtes-vous sûr de vouloir supprimer cette localité ?</h5>
              {localiteToDelete && (
                <div className="mt-3 p-3 bg-light rounded">
                  <strong>{localiteToDelete.nom}</strong><br />
                  <small className="text-muted">ID: {localiteToDelete.id}</small>
                  <div className="mt-2">{getTypeBadge(localiteToDelete.type_code)}</div>
                </div>
              )}
              <p className="text-muted mt-3">Cette action est irréversible. Toutes les localités enfants seront également affectées.</p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={loadingDelete}><X className="me-2" />Annuler</Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={loadingDelete}>
              {loadingDelete ? <><Spinner animation="border" size="sm" className="me-2" />Suppression...</> : <><Trash className="me-2" />Supprimer</>}
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
          .stats-card { transition: transform 0.3s ease, box-shadow 0.3s ease; border-radius: 12px; overflow: hidden; }
          .stats-card:hover { transform: translateY(-5px); box-shadow: 0 10px 30px rgba(0,0,0,0.1) !important; }
          .card-gradient { position: absolute; top: 0; left: 0; width: 100%; height: 4px; opacity: 0.8; }
          .hover-bg-light:hover { background-color: #f8f9fa !important; }
          .cursor-pointer { cursor: pointer; }
          .rounded-circle { border-radius: 50% !important; }
        `}</style>
        <Footer />
      </div>
    </React.Fragment>
  );
}